import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Briefcase, Award, MapPin } from 'lucide-react';
import type { PassportStation, StationType } from '../../api/passport';
import { isPresent, sortStationsByRecency, formatStationYear } from './stationOrder';
import PassportAiNote from './PassportAiNote';

interface StationsViewProps {
  stations: PassportStation[];
  className?: string;
}

const TYPE_META: Record<StationType, {
  Icon: React.ComponentType<{ className?: string }>;
  ring: string;
  chip: string;
  line: string;
}> = {
  education: {
    Icon: GraduationCap,
    ring: 'from-pink-400 to-rose-500',
    chip: 'bg-pink-50 text-pink-600 border-pink-100',
    line: 'bg-pink-200',
  },
  work: {
    Icon: Briefcase,
    ring: 'from-rose-500 to-rose-700',
    chip: 'bg-rose-50 text-rose-600 border-rose-100',
    line: 'bg-rose-200',
  },
  other: {
    Icon: Award,
    ring: 'from-amber-300 to-yellow-500',
    chip: 'bg-amber-50 text-amber-600 border-amber-100',
    line: 'bg-amber-200',
  },
};

const locationLabel = (s: PassportStation): string | null => {
  if (s.city && s.country) return `${s.city}, ${s.country}`;
  return s.city || s.country || null;
};

const StationsView: React.FC<StationsViewProps> = ({ stations, className = '' }) => {
  if (!stations || stations.length === 0) {
    return (
      <div className={`flex items-center justify-center py-16 text-slate-400 ${className}`}>
        <div className="text-center">
          <div className="text-2xl mb-1.5">🧭</div>
          <p className="text-xs">No stations yet</p>
        </div>
      </div>
    );
  }

  const ordered = sortStationsByRecency(stations);

  return (
    <div className={`relative max-w-2xl mx-auto px-4 py-5 sm:py-6 ${className}`}>
      <PassportAiNote />
      <div className="relative">
        {ordered.map((s, i) => {
          const meta = TYPE_META[s.type] || TYPE_META.other;
          const { Icon } = meta;
          const isLast = i === ordered.length - 1;
          const present = isPresent(s.year);
          const loc = locationLabel(s);

          return (
            <motion.div
              key={`${i}-${s.title ?? ''}`}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
              className="relative flex gap-2.5 sm:gap-4 pb-4 sm:pb-6 last:pb-0"
            >
              {/* Rail + node */}
              <div className="flex flex-col items-center flex-shrink-0">
                <div className={`w-7 h-7 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br ${meta.ring} flex items-center justify-center text-white shadow-sm`}>
                  <Icon className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px]" />
                </div>
                {!isLast && <div className={`w-[2px] flex-1 mt-1 ${meta.line} rounded-full`} />}
              </div>

              {/* Card */}
              <div className="flex-1 min-w-0 bg-white rounded-xl border border-slate-200 shadow-[0_1px_3px_rgba(0,0,0,0.03)] p-2.5 sm:p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-[13px] sm:text-[15px] font-bold text-slate-800 leading-snug truncate">
                    {s.title || 'Untitled'}
                  </h3>
                  {s.year && (
                    <span
                      className={`flex-shrink-0 text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                        present ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : meta.chip
                      }`}
                    >
                      {formatStationYear(s.year)}
                    </span>
                  )}
                </div>
                {s.org && (
                  <p className="text-[12px] sm:text-sm text-slate-600 mt-0.5 truncate">{s.org}</p>
                )}
                {loc && (
                  <p className="flex items-center gap-1 text-[10px] sm:text-xs text-slate-400 mt-1">
                    <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" /> {loc}
                  </p>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default StationsView;
